import { useEffect } from "react";
import { Form } from "antd";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import TimePicker from "@/common/elements/TimePicker";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { useState } from "react";
import { Popconfirm } from "antd";
import ResponseDetailsForm from "./ResponseDetailForm";
import dayjs from "dayjs";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const WARRANTS_STATE_RESPONSES_HEADERS = [
  { key: "id",       text: "ID"        },
  { key: "received", text: "Received"  },
  { key: "error",    text: "Error?"    },
  { key: "response", text: "Response"  },
];

/**
 * StateSubmissionDetails
 * Receives a `submission` object (one item from warrantsStateSubmissions[])
 * and displays it read-only (submissions come from the state system,
 * they are not edited locally).
 */
const StateSubmissionDetails = ({ submission = null }) => {
  const [form] = Form.useForm();
  const [isResponseModal, setIsResponseModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [selectedResponseIndex, setSelectedResponseIndex] = useState(null);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This tab is only reachable
  // from the "Warrants New" module (single-warrant detail view), so the
  // gridId is hardcoded the same way Bookings' grids are.
  const {
    headers: responseHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Warrants New:WarrantsStateResponses",
    WARRANTS_STATE_RESPONSES_HEADERS
  );

  useEffect(() => {
    if (!submission) return;
    form.setFieldsValue({
      transactionType: submission.transactionType ?? "",
      dateSubmitted:   submission.dateSubmitted
        ? dayjs(submission.dateSubmitted)
        : null,
      timeSubmitted:   submission.timeSubmitted ?? "",
      request:         submission.request ?? "",
    });
  }, [submission]);

  const responses = submission?.responses ?? [];

  const responseRows = responses.map((r, i) => ({
    _index:   i,
    id:       r.id       ?? "",
    received: r.received ?? "",
    error:    r.error    ?? "",
    response: r.response ?? "",
  }));

  const handleResponseRowClick = (data, index) => {
    if (index === undefined || index === null) return;
    setSelectedResponseIndex(data?._index ?? index);
  };

  const handleResponseDetails = () => {
    if (selectedResponseIndex === null) return;
    setSelectedResponse(responses[selectedResponseIndex]);
    setIsResponseModal(true);
  };

  return (
    <>
      <StyledSectionHeading>
        <span>Request Information</span>
      </StyledSectionHeading>

      {/* Read-only — submissions are generated by the state system */}
      <Form form={form} disabled>
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={8}>
            <FormItem label="Transaction Type" name="transactionType">
              <TextField />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Date Submitted" name="dateSubmitted">
              <CommonDatePicker showTime={false} />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={8}>
            <FormItem label="Time Submitted" name="timeSubmitted">
              <TimePicker />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={24}>
            <FormItem label="Request" name="request">
              <TextField rows="6" inputType="textarea" />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>

      <StyledSectionHeading>
        <span>Response(s)</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex justify="space-between" align="center">
          <h2>Response from State and Federal Agencies</h2>
          <Button
            style={{ marginBottom: "5px" }}
            onClick={handleResponseDetails}
            disabled={selectedResponseIndex === null || isConfiguring}
          >
            Details
          </Button>

          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button
              style={{ marginBottom: "5px" }}
              htmlType="button"
              onClick={startConfiguring}
            >
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Save the current column layout?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ marginBottom: "5px" }} htmlType="button">
                {configButtonLabel}
              </Button>
            </Popconfirm>
          )}
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={responseHeaders}
          draggable={isConfiguring}
          data={responseRows}
          rowUniqueId="_index"
          onRowClick={handleResponseRowClick}
          onRowDoubleClick={(data) => {
            const idx = data?._index;
            if (idx !== undefined) {
              setSelectedResponse(responses[idx]);
              setIsResponseModal(true);
            }
          }}
        />
      </StyledSubTableContainer>

      {isResponseModal && (
        <CustomModal
          header="Response Details"
          show={isResponseModal}
          size="md"
          onHide={() => {
            setIsResponseModal(false);
            setSelectedResponse(null);
            setSelectedResponseIndex(null);
          }}
        >
          <ResponseDetailsForm response={selectedResponse} />
        </CustomModal>
      )}
    </>
  );
};

export default StateSubmissionDetails;
